<template>
  <div
    class="fts-history"
    :data-history-load="loading ? 'loading' : 'loaded'"
    v-if="show"
  >
    <!-- history header -->
    <div class="history-header">
      <div class="history-header-container">
        <div
          class="history-header-wrapper"
          @click="toggleMode('comment')"
          :class="{
            'history-header-wrapper--active': mode === 'comment',
          }"
          v-if="commentSection"
        >
          Comment
        </div>
        <div
          class="history-header-wrapper"
          @click="toggleMode('history')"
          :class="{
            'history-header-wrapper--active': mode === 'history',
          }"
        >
          History
        </div>
        <div 
          class="history-header-wrapper" 
          :class="{
            'history-header-wrapper--active': mode === 'notification',
          }" 
          @click="toggleMode('notification')" 
          v-if="enableNotification"
        >
          Notification
        </div>
      </div>
    </div>
    <!-- history section -->
    <div
      class="history-section fts-scrollbar fts-scrollbar-overlay"
      ref="historySection"
    >
      <div key="load-archive" class="fts-text-center" v-if="historyArchive && !historyLoadMore">
        <fts-btn
          text
          text-transform-init
          :loading="loading"
          class="history-load-btn fts-font-normal"
          @click="loadArchive"
        >
          Older history is archived, click to show
        </fts-btn>
      </div>

      <fts-spinner height="100%" v-if="initLoading" />

      <div class="history-section-intro" key="initial-notification" v-else-if="!locusId && mode == 'notification'">
        <font-awesome-icon :icon="['far', 'bell']" class="history-section-intro-icon" />
        <div class="history-section-intro-title">
          Notification status tracking
        </div>
        <div class="history-section-intro-description">
          Stay informed with notification status tracking for timely updates.
        </div>
      </div>
      
      <div class="history-section-intro" key="initial-history" v-else-if="!locusId && mode == 'history'">
        <font-awesome-icon :icon="['fas', 'clock-rotate-left']" class="history-section-intro-icon" />
        <div class="history-section-intro-title">
          See changes to this record
        </div>
        <div class="history-section-intro-description">
          Configuration changes
        </div>
      </div>

      <div class="history-section-intro" key="initial-comment" v-else-if="!locusId">
        <font-awesome-icon :icon="['far', 'message']" class="history-section-intro-icon" />
        <div class="history-section-intro-title">
          Start a conversation
        </div>
        <div class="history-section-intro-description">
          Let's create an entry to initiate collaboration with your team.
        </div>
      </div>
      
      <template v-else-if="mode === 'notification' && notification && notification.length">
        <fts-history-notification
          v-for="x in notification"
          :key="x.id"
          :users="users"
          :data="x"
        />
      </template>

      <div class="history-section-intro" key="empty-notification" v-else-if="mode == 'notification'">
        <font-awesome-icon :icon="['far', 'bell']" class="history-section-intro-icon" />
        <div class="history-section-intro-title">
          Notification status tracking
        </div>
        <div class="history-section-intro-description">
          Stay informed with notification status tracking for timely updates.
        </div>
      </div>

      <template v-else-if="(histories && histories.length)">
        <div key="load-more" class="fts-text-center" v-if="historyLoadMore">
          <fts-btn
            text
            text-transform-init
            :loading="loading"
            class="history-load-btn fts-font-normal"
            @click="nextPage"
          >
            more histories
          </fts-btn>
        </div>
        <div
          v-for="(history, historyIndex) in histories"
          :key="history.id"
          class="history-wrapper"
        >
          <!-- the day is stated once for the run of entries that share it, so rows can
               carry just a clock time instead of repeating the full date -->
          <div class="history-day" v-if="dayDividers[historyIndex]">
            {{ dayDividers[historyIndex] }}
          </div>

          <fts-history-content
            :data.sync="histories[historyIndex]"
            :vector-id="vectorId"
            :locus-id="locusId"
            :states="states"
            :users="users"
            :comment-section="commentSection"
            :mode="mode"
            :is-last="historyIndex == histories.length - 1"
            :continues-actor="actorContinuations[historyIndex]"
            :data-index="historyIndex"
            @comment:reply="replyComment"
          />
          <template
            v-if="
              commentSection &&
              history.historyItems &&
              history.historyItems.length
            "
          >
            <fts-history-content
              v-for="(item, itemIndex) in history.historyItems"
              :key="item.id"
              :data.sync="history.historyItems[itemIndex]"
              :vector-id="vectorId"
              :locus-id="locusId"
              :states="states"
              :users="users"
              child
              :comment-section="commentSection" 
              :mode="mode" 
              @comment:reply="replyComment"
            />
          </template>
        </div>
      </template>

      <div class="history-section-intro" key="empty-history" v-else-if="mode == 'history'">
        <font-awesome-icon :icon="['fas', 'clock-rotate-left']" class="history-section-intro-icon" />
        <div class="history-section-intro-title">
          See changes to this record
        </div>
        <div class="history-section-intro-description">
          Configuration changes
        </div>
      </div>

      <div class="history-section-intro" key="empty-comment" v-else>
        <font-awesome-icon :icon="['far', 'message']" class="history-section-intro-icon" />
        <div class="history-section-intro-title">
          Start a conversation
        </div>
        <div class="history-section-intro-description">
          Share thoughts, react to comments, and collaboration with your team.
        </div>
      </div>
    </div>
    <!-- history footer -->
    <div class="history-footer" v-if="commentSection && locusId && mode !== 'notification'">
      <fts-comment
        :locus-id="locusId"
        :vector-id="vectorId"
        :avatar="false"
        :send-action="true"
        v-model="collaboration"
        @added="addHistory($event)"
        @submitting="updateHistorySubmitQueue"
        ref="historySectionComment"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment';

import { classificationStore } from '../../store/classificationStore';
import { dayLabel } from '../../commons/relativeDay';

import FTSBtn from '../FTSBtn';
import FTSComment from '../FTSComment';
import FTSSpinner from '../FTSSpinner';
import FTSHistoryContent from './FTSHistoryContent';
import FTSHistoryNotification from './FTSHistoryNotification';

const MESSAGE = Object.freeze({
  HISTORY_SUBMIT_QUEUE: "Please wait for the comment to be submitted."
});

const MODE = Object.freeze({
  COMMENT: "comment",
  HISTORY: "history"
});

export default {
  props: {
    locusId: [String, Number],
    vectorId: [String, Number],
    states: Array,
    users: Array,
    commentSection: {
      type: Boolean,
      default: true,
    },
    enableNotification: {
      type: Boolean,
      default: true
    },
    initOpen: Boolean
  },
  components: {
    "fts-btn": FTSBtn,
    "fts-comment": FTSComment,
    "fts-spinner": FTSSpinner,
    "fts-history-content": FTSHistoryContent,
    "fts-history-notification": FTSHistoryNotification,
  },
  data: () => ({
    show: false,
    initLoading: false,
    loading: false,
    mode: MODE.COMMENT,
    histories: [],
    historyCurrentPage: 0,
    historyLoadMore: false,
    historyArchive: false,
    // Sticky once the reader opens the archived tail: it pages on its own counter and every
    // later page has to keep asking for it (SS05770).
    historyWithArchive: false,
    notification: [],
    collaboration: { attachments: [] },
    historySubmitQueue: []
  }),
  computed: {
    // Per index: the day label to print above the entry, or null when the entry shares
    // its day with the one before it.
    dayDividers() {
      let previous = null;

      return (this.histories || []).map(history => {
        // grouped on the calendar day itself, not on the label — two days must never
        // collapse into one run just because they render the same words
        const key = history.on ? moment(history.on).format('YYYY-MM-DD') : null;
        if (!key || key === previous) {
          return null;
        }

        previous = key;
        return dayLabel(history.on);
      });
    },
    // Per index: true when this entry continues a run by the same actor on the same day,
    // in which case the row suppresses its avatar and name. A day divider always breaks
    // the run so a group never straddles the label.
    actorContinuations() {
      const days = this.dayDividers;

      return (this.histories || []).map((history, index) => {
        if (index === 0 || days[index]) {
          return false;
        }

        const previous = this.histories[index - 1];
        return Boolean(previous && history.by && previous.by === history.by);
      });
    }
  },
  methods: {
    resetScroll() {
      this.$refs.historySection && this.$refs.historySection.scrollTo(0, 0);
    },
    replyComment(content) {
      if (this.historySubmitQueue.length) {
        this.$toasted.info(MESSAGE.HISTORY_SUBMIT_QUEUE);
      } else {
        Boolean(content) && this.$set(this.collaboration, "content", content);
        this.focusSectionComment();
      }
    },
    updateHistorySubmitQueue(item) {
      if (item.isSubmitting) {
        this.historySubmitQueue.push(item.id);
      } else {
        const idx = this.historySubmitQueue.indexOf(item.id);
        idx > -1 && this.historySubmitQueue.splice(idx, 1);
      }
    },
    addHistory(item) {
      this.histories.push(item);
      this.$emit("added");
      this.$refs.historySection && this.$nextTick(() => {
        this.$refs.historySection.scrollTop = this.$refs.historySection.scrollHeight;
      });
    },
    // P2 live propagation (FETIAS docs/collaboration-comment-edit-delete-proposal.md §9): the
    // server pushes a content-free `commentChanged` frame to the workspace group on every comment
    // create/edit/delete. When this panel shows that entry, refetch its first page silently (no
    // spinner, no scroll reset). Frames from the viewer's own actions are skipped — the optimistic
    // update already applied and a refetch could race an in-flight submit.
    _onCommentChanged(payload) {
      if (!payload || !this.show || !this.locusId) return;
      if (String(payload.entryId) !== String(this.locusId)) return;
      if (payload.by && payload.by === classificationStore.state.currentUsername) return;
      if (this.historySubmitQueue.length) return;

      clearTimeout(this._commentChangedTimer);
      this._commentChangedTimer = setTimeout(() => this.refreshFromPush(), 400);
    },
    async refreshFromPush() {
      if (!this.show || this.mode === 'notification' || this.historyCurrentPage !== 0) return;
      // The archived tail is a manual, merged read — a silent page-0 refetch would drop it.
      if (this.historyWithArchive) return;
      const historyMode = this.mode;

      try {
        const res = await classificationStore.dispatch("retrieveHistory", {
          moduleId: this.vectorId,
          id: this.locusId,
          history: historyMode == MODE.HISTORY,
          currentPage: 0
        });

        if (historyMode != this.mode || this.historyCurrentPage !== 0) return;

        this.histories = res.data.histories;
        this.historyLoadMore = Boolean(res.data.hasMore);
      } catch (err) {
        console.error(err);
      }
    },
    async retrieveHistory(loadMore, withArchive) {
      if (this.locusId && this.show) {
        const historyMode = this.mode;
        this.loading = true;
        // Opening the archive continues the timeline instead of restarting it: `a=true` returns
        // archived rows only, paged on their own counter, and the first archived page is merged
        // into the live rows already on screen.
        const enteringArchive = Boolean(withArchive) && !this.historyWithArchive;
        if (enteringArchive) {
          this.historyWithArchive = true;
          this.historyCurrentPage = 0;
        } else if (loadMore) {
          this.historyCurrentPage += 1;
        } else {
          this.historyWithArchive = false;
          this.resetScroll();
          this.historyCurrentPage = 0;
        }

        try {
          const res = await classificationStore.dispatch("retrieveHistory", {
            moduleId: this.vectorId,
            id: this.locusId,
            history: historyMode == MODE.HISTORY,
            currentPage: this.historyCurrentPage,
            archive: this.historyWithArchive
          });

          if (historyMode != this.mode) return;

          if (loadMore || enteringArchive) {
            const firstEl = document.querySelector('[data-index="0"]')
            const firstElOffsetTop = firstEl ? firstEl.offsetTop : 0;
            this.histories = _.sortBy([
              ...res.data.histories.filter((x) => !this.histories.some((y) => y.id === x.id)),
              ...this.histories
            ], 'on');

            this.$nextTick(() => {
              if (firstEl) this.$refs.historySection.scrollTo(0, firstEl.offsetTop - firstElOffsetTop)
            });
          } else {
            this.histories = res.data.histories;
          }

          this.historyLoadMore = Boolean(res.data.hasMore);
          this.historyArchive = Boolean(res.data.hasArchive);
        } catch (err) {
          console.error(err);
        } finally {
          this.loading = false;
        }
      }
    },
    async retrieveNotification() {
      if (this.locusId && this.show) {
        this.loading = true;
        this.resetScroll();

        try {
          let res = await classificationStore.dispatch("retrieveNotification", {
            moduleId: this.vectorId,
            id: this.locusId,
          });
          this.notification = res.data.notifications;
        } catch (err) {
          console.error(err);
        } finally {
          this.loading = false;
        }
      }
    },
    nextPage() {
      if (!this.loading) {
        this.retrieveHistory(true);
      }
    },
    loadArchive() {
      if (!this.loading) {
        this.retrieveHistory(false, true);
      }
    },
    async toggleHistory(mode) {
      const currentMode = this.mode;
      const isShow = this.show;

      if ((mode === currentMode || !mode) && isShow) {
        this.show = false;
      } else if (currentMode !== mode && isShow) {
        
        if (mode) {
          this.mode = mode;
        }

        this.show = true;
        this.initLoading = true;
        await this.retrieveHistory();
        this.initLoading = false;
      } else if (!isShow) {

        if (mode) {
          this.mode = mode;
        }

        this.show = true;
        this.initLoading = true;
        await this.retrieveHistory();
        this.initLoading = false;
      }
    },
    async toggleMode(mode) {
      if (this.historySubmitQueue.length) {
        this.$toasted.info(MESSAGE.HISTORY_SUBMIT_QUEUE);
      } else if (this.mode !== mode) {
        this.initLoading = true;
        this.mode = mode;
        this.collaboration = { attachments: [] };
        if (this.mode === "notification") {
          await this.retrieveNotification();
        } else {
          await this.retrieveHistory();
        }
        this.initLoading = false;
      }
    },
    focusSectionComment() {
      if (!this.$refs.historySectionComment.showComment) {
        this.$refs.historySectionComment.showComment = true;
      }
      this.$refs.historySectionComment.onFocus();
    }
  },
  mounted() {
    if (!this.commentSection) {
      this.mode = MODE.HISTORY;
    }

    if (this.initOpen) {
      this.toggleHistory();
    }

    if (window.eventBus) {
      window.eventBus.$on("signalr.commentChanged", this._onCommentChanged);
    }
  },
  beforeDestroy() {
    clearTimeout(this._commentChangedTimer);
    if (window.eventBus) {
      window.eventBus.$off("signalr.commentChanged", this._onCommentChanged);
    }
  }
};
</script>

<style lang="scss" scoped>
@use "./src/assets/scss/vars" as *;

.fts-history {
  $width: 400px !default;
  flex: 1 1 $width;
  max-width: $width;
  min-width: $width;
  display: flex;
  flex-direction: column;
  border-left: 1px solid var(--fts-border);
  background-color: color-mix(in srgb, var(--fts-card-background) 70%, var(--fts-background));
  .history-header {
    min-height: 47px;
    display: flex;
    align-items: flex-end;
    padding: 0.5rem 0.75rem 0;
    .history-header-container {
      display: flex;
      align-items: center;
      width: 100%;
      margin: 0 -0.75rem;
      padding: 0 0.75rem;
      border-bottom: 1px solid var(--fts-border);
      .history-header-wrapper {
        padding-bottom: 0.5rem;
        border-bottom: 2px solid transparent;
        font-weight: 500;
        font-size: var(--inistate-label-text-14);
        line-height: var(--inistate-paragraph-text-14--line-height);
        color: rgba(var(--fts-default-rgb), 0.62);
        transition: color 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        cursor: pointer;
        &:only-child {
          cursor: default;
        }
        &:not(:last-child) {
          margin-right: 1rem;
        }
        &:hover {
          color: rgba(var(--fts-default-rgb), 0.85);
        }
        &.history-header-wrapper--active {
          border-color: var(--fts-primary);
          color: var(--fts-primary);
        }
      }
    }
  }
  .history-section {
    flex: 1;
    position: relative;
    padding-bottom: 0.5rem;
  }
  // quiet micro-label, in the same tier as the row timestamps and the panel's badges
  .history-day {
    padding: 20px 14px 6px;
    font-weight: 500;
    font-size: 0.625rem;
    line-height: 1rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--fts-gray-400);
  }
  .history-section-intro {
    height: 100%;
    @include flex-center-middle;
    flex-direction: column;
    padding: 3rem 2rem;
    text-align: center;
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
  }
  .history-section-intro-icon {
    margin-bottom: 0.75rem;
    font-size: 1.5rem;
    color: var(--fts-default);
  }
  .history-section-intro-title {
    margin-bottom: 0.25rem;
    color: var(--fts-text);
  }
  .history-section-intro-description {
    color: var(--fts-default);
  }
  .history-footer {
    padding: 0.5rem;
  }
  .history-load-btn {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }
}
</style>